/**
 * VerticalSplit — two stacked panes with a draggable horizontal divider.
 *
 * The complement of ResizablePanel (which is horizontal-only and collapse-
 * focused). No collapse here — just a row-resize divider. The TOP pane's share
 * is stored as a fraction (0..1) of the container height under `storageKey`, so
 * it survives reloads and adapts to any container size.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import { hostStorage } from "../host/hostStorage.js";

function readFraction(key: string | undefined, fallback: number): number {
  if (!key) return fallback;
  const v = Number(hostStorage.getItem(key));
  return Number.isFinite(v) && v > 0.05 && v < 0.95 ? v : fallback;
}

export interface VerticalSplitProps {
  readonly top: ReactNode;
  readonly bottom: ReactNode;
  /** Persist the top pane's height fraction under this storage key. */
  readonly storageKey?: string;
  readonly defaultFraction?: number;
  /** Minimum pixel height each pane is allowed to shrink to. */
  readonly minPx?: number;
  readonly className?: string;
}

export function VerticalSplit({
  top,
  bottom,
  storageKey,
  defaultFraction = 0.5,
  minPx = 120,
  className,
}: VerticalSplitProps): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [fraction, setFraction] = useState(() => readFraction(storageKey, defaultFraction));

  useEffect(() => {
    if (!storageKey) return;
    hostStorage.setItem(storageKey, String(fraction));
  }, [storageKey, fraction]);

  const onDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const onMove = (ev: PointerEvent): void => {
        const H = rect.height;
        if (H <= 0) return;
        const minF = minPx / H;
        const maxF = 1 - minF;
        const f = (ev.clientY - rect.top) / H;
        setFraction(Math.min(maxF, Math.max(minF, f)));
      };
      const onUp = (): void => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [minPx],
  );

  return (
    <div ref={ref} className={`flex min-w-0 flex-1 flex-col overflow-hidden ${className ?? ""}`}>
      {/* Each slot is itself a flex column AND a positioning context, so any
          child honours the LayoutRegion contract: `flex-1` fills the slot's
          height and `absolute inset-0` resolves against it. Hand-written
          `relative h-full` wrappers (which silently zeroed the board's height)
          are no longer needed or allowed at call sites. */}
      <div
        style={{ height: `${(fraction * 100).toFixed(3)}%` }}
        className="relative flex min-h-0 shrink-0 flex-col overflow-hidden"
      >
        {top}
      </div>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize panes"
        title="Drag to resize"
        onPointerDown={onDown}
        className="relative h-px shrink-0 cursor-row-resize bg-separator transition-colors hover:bg-arch-500/60"
      >
        {/* Wider invisible grab area around the 1px line. */}
        <span className="absolute inset-x-0 -top-1 -bottom-1" />
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">{bottom}</div>
    </div>
  );
}
